import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatStepper } from '@angular/material/stepper';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { NOTIFICATION_TYPE, NotificationEvent, NotificationService } from '../../../services/NotificationService';
import { SessionService } from '../../../session-service.service';
import { StringTemplates, TemplateService } from '../../../services/TemplateService';
import { ProductTypeEnum } from '../../../model/enums/ProductTypeEnum';
import { ApplicationApiService } from '../../../services/applicationApiService';
import { SessionGgService } from '../../../services/session-gg.service';
import { CalculationResponseResultPremiumDto } from '../../../model/business/calculation/CalculationResponseResultPremiumDto';
import { ConfigSettingDto } from '../../../model/business/ConfigSettingDto';

@Component({
  selector: 'app-final-page-gg',
  templateUrl: './summary-page.component.html',
  styleUrls: ['./summary-page.component.css']
})
export class SummaryPageComponent implements OnInit, OnDestroy {
  policyTerms: string;
  productType = ProductTypeEnum.GG;
  formGroup: FormGroup;
  premiumSubscription: Subscription;
  premiums: CalculationResponseResultPremiumDto | undefined;

  notificationEvent: NotificationEvent;
  notificationSubscription: Subscription;

  @Input()
  stepper: MatStepper;

  @Output()
  submitClicked = new EventEmitter();

  @Output()
  updateApplicationDto = new EventEmitter();

  @Output()
  finishForm = new EventEmitter();

  @Output()
  downloadQuote = new EventEmitter<void>();

  @Output()
  submitApplicationRequest = new EventEmitter<void>();

  constructor(
    private sessionService: SessionService,
    private translateService: TranslateService,
    private fb: FormBuilder,
    private templateService: TemplateService,
    private applicationApiService: ApplicationApiService,
    private sessionGgService: SessionGgService,
    private notificationService: NotificationService,
  ) {}

  ngOnInit(): void {
    this.premiumSubscription = this.sessionService.getCalculationResponse.subscribe((premiums) => {
      this.premiums = premiums;
      this.policyTerms = premiums.policyTerms;
    });
    this.formGroup = this.fb.group({
      isAgreed: ['', Validators.requiredTrue],
    });

    this.listenToNotifications();
  }

  ngOnDestroy(): void {
    this.premiumSubscription.unsubscribe();
    this.notificationSubscription.unsubscribe();
  }

  listenToNotifications(): void {
    this.notificationSubscription = this.notificationService.register().subscribe((event) => {
      this.notificationEvent = event;
    });
  }

  parseTemplatedStatement(statement: string, template: StringTemplates | Array<StringTemplates>): string {
    return this.templateService.parseTemplatedStatement(statement, template, {
      policyTerms: this.policyTerms,
      intermediaryName: this.sessionService.intermediaryName,
      intermediaryFee: this.sessionService.intermediaryFee,
      intermediarySubscriptionFee: this.sessionService.intermediarySubscriptionFee,
    });
  }

  getTextFor8Paragraph(): string {
    if (this.sessionService.getConfigSettingsDto().showIntermediaryFee && this.sessionService.getConfigSettingsDto().showIntermediarySubscriptionFee) {
      return this.parseTemplatedStatement('ClosingStatement.Bullet8_withSubscriptionFee', ['name_intermediary', 'intermediary_subscription_fee']);
    }
    if (!this.sessionService.getConfigSettingsDto().showIntermediaryFee && this.sessionService.getConfigSettingsDto().showIntermediarySubscriptionFee) {
      return this.parseTemplatedStatement('ClosingStatement.Bullet8_withFee', ['name_intermediary', 'intermediary_fee']);
    }
    if (this.sessionService.getConfigSettingsDto().showIntermediaryFee && !this.sessionService.getConfigSettingsDto().showIntermediarySubscriptionFee) {
      return this.parseTemplatedStatement('ClosingStatement.Bullet8_withoutSubscriptionFee', ['name_intermediary']);
    }
    return '';
  }
  getTextForFinalStatementAddition (): string {
    return this.sessionService.getConfigSettingsDto().finalStatementAddition
  }

  submitApplication(): void {
    this.notificationEvent = {
      type: NOTIFICATION_TYPE.HIDDEN,
    };

    if (this.formGroup.get('isAgreed').valid) {
      this.updateApplicationDto.emit();
      this.submitApplicationRequest.emit();
    } else {
      this.formGroup.get('isAgreed').markAsTouched();
      this.translateService.get('Common.ValidationFailedMessage').subscribe(message => {
        this.notificationEvent = {
          type: NOTIFICATION_TYPE.WARNING,
          messages: [message],
        };
      });
    }
  }

  get configSettings(): ConfigSettingDto {
    return this.sessionService.getConfigSettingsDto();
  }

  get getShowQuote(): boolean {
    return !!this.premiums.premium;
  }

  handleDownloadQuote(): void {
    this.downloadQuote.emit();
  }
}
